import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import { accountEvents } from '../auth/accountEvents';
import {
  FaHome, FaArrowDown, FaArrowUp, FaHistory,
  FaTicketAlt, FaUser, FaShieldAlt, FaIdCard,
  FaSignOutAlt, FaBars, FaTimes, FaCheck, FaClock,
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaEdit, FaSync, FaPaperclip, FaChevronRight, FaUserFriends,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { TicketListSkeleton, TransactionListSkeleton } from '../components/MatchCardSkeleton';
import './UserAccountPage.css';

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return isToday ? `Auj. ${time}` : `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} ${time}`;
}

function Spinner() {
  return <span className="ua-spinner" />;
}

function StatusBadge({ status }) {
  const map = {
    pending:  { label: 'En attente', cls: 'badge-pending' },
    approved: { label: 'Approuvé',   cls: 'badge-approved' },
    rejected: { label: 'Refusé',     cls: 'badge-rejected' },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`ua-status-badge ${s.cls}`}>{s.label}</span>;
}

/* ═══════════════════════════════════════════════
   SECTION: OVERVIEW
═══════════════════════════════════════════════ */
function OverviewSection({ user, balance, onNavigate }) {
  const initials = (user?.username ?? 'U').slice(0, 2).toUpperCase();
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="ua-section">
      {/* Welcome banner */}
      <div className="ua-welcome-card">
        <div className="ua-welcome-avatar">{initials}</div>
        <div className="ua-welcome-info">
          <h2 className="ua-welcome-name">Bonjour, {user?.username ?? 'Joueur'} 👋</h2>
          <p className="ua-welcome-date">{today.charAt(0).toUpperCase() + today.slice(1)}</p>
        </div>
        <div className="ua-welcome-balance">
          <p className="ua-bal-label">Solde</p>
          <p className="ua-bal-value">{Number(balance).toLocaleString('fr-FR')} FC</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="ua-quick-grid">
        <button className="ua-quick-card ua-qc-deposit" onClick={() => onNavigate('deposit')}>
          <div className="ua-qc-icon-wrap"><FaArrowDown /></div>
          <div>
            <p className="ua-qc-label">Déposer</p>
            <p className="ua-qc-sub">Via M-Pesa</p>
          </div>
        </button>
        <button className="ua-quick-card ua-qc-withdraw" onClick={() => onNavigate('withdraw')}>
          <div className="ua-qc-icon-wrap"><FaArrowUp /></div>
          <div>
            <p className="ua-qc-label">Retirer</p>
            <p className="ua-qc-sub">Vers M-Pesa</p>
          </div>
        </button>
        <button className="ua-quick-card ua-qc-bets" onClick={() => onNavigate('bets')}>
          <div className="ua-qc-icon-wrap"><FaTicketAlt /></div>
          <div>
            <p className="ua-qc-label">Mes Paris</p>
            <p className="ua-qc-sub">Tickets actifs</p>
          </div>
        </button>
        <button className="ua-quick-card ua-qc-history" onClick={() => onNavigate('deposits')}>
          <div className="ua-qc-icon-wrap"><FaHistory /></div>
          <div>
            <p className="ua-qc-label">Historique</p>
            <p className="ua-qc-sub">Transactions</p>
          </div>
        </button>
      </div>

      {/* Info cards */}
      <div className="ua-info-grid">
        <div className="ua-info-card">
          <p className="ua-info-label">Email</p>
          <p className="ua-info-val">{user?.email ?? '—'}</p>
        </div>
        <div className="ua-info-card">
          <p className="ua-info-label">Téléphone</p>
          <p className="ua-info-val">{user?.phone_number ?? '—'}</p>
        </div>
        <div className="ua-info-card">
          <p className="ua-info-label">Membre depuis</p>
          <p className="ua-info-val">{formatDate(user?.created_at)}</p>
        </div>
        <div className="ua-info-card">
          <p className="ua-info-label">Statut</p>
          <p className="ua-info-val">
            {user?.is_active
              ? <span className="ua-active-badge"><FaCheck size={10} /> Actif</span>
              : <span className="ua-inactive-badge">Inactif</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: DEPOSIT
═══════════════════════════════════════════════ */
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

const OPERATORS = [
  {
    id: 'airtel',
    name: 'Airtel Money',
    number: '0961087122',
    display: '096 108 7122',
    owner: 'Matobue Dinyanda Theodor',
    color: '#e53935',
    bg: 'rgba(229,57,53,0.08)',
    border: 'rgba(229,57,53,0.35)',
  },
  {
    id: 'vodacom',
    name: 'M-Pesa Vodacom',
    number: '+243826097974',
    display: '+243 826 097 974',
    owner: 'AfriMoney',
    color: '#1565c0',
    bg: 'rgba(21,101,192,0.08)',
    border: 'rgba(21,101,192,0.35)',
  },
];

function DepositSection({ accessToken, onSuccess, initialOperator }) {
  const fileRef = useRef(null);
  const [selected, setSelected]   = useState(
    OPERATORS.find(o => o.id === initialOperator) || OPERATORS[0]
  );
  const [copiedId, setCopiedId]   = useState(null);
  const [amount, setAmount]       = useState('');
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [step, setStep]           = useState('form');
  const [errorMsg, setErrorMsg]   = useState('');
  const [deposit, setDeposit]     = useState(null);

  const reset = () => {
    setAmount(''); setFile(null); setPreview(null);
    setStep('form'); setErrorMsg(''); setDeposit(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const copy = (op) => {
    navigator.clipboard.writeText(op.number).then(() => {
      setCopiedId(op.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type)) { setErrorMsg('Format non accepté (JPEG, PNG, WebP, HEIC, HEIF).'); return; }
    if (f.size > MAX_SIZE) { setErrorMsg('Fichier trop grand (max 10 Mo).'); return; }
    setErrorMsg(''); setFile(f); setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) { setErrorMsg('Montant invalide.'); return; }
    if (parseFloat(amount) < 10000) { setErrorMsg('Montant minimum : 10 000 FC.'); return; }
    if (!file) { setErrorMsg('Capture d\'écran requise.'); return; }
    // Auto-generate reference from selected operator
    const autoReference = selected.id.toUpperCase();
    setErrorMsg('');
    try {
      setStep('uploading');
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await fetch(`${API_BASE_URL}/api/v1/uploads/image`, {
        method: 'POST', credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` }, body: fd,
      });
      if (!upRes.ok) { const e = await upRes.json().catch(() => ({})); throw new Error(e.detail || 'Upload échoué'); }
      const { url: proofUrl } = await upRes.json();
      setStep('submitting');
      const depRes = await fetch(`${API_BASE_URL}/api/v1/deposits`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ amount: parseFloat(amount), reference: autoReference, proof_image: proofUrl }),
      });
      if (!depRes.ok) { const e = await depRes.json().catch(() => ({})); throw new Error(e.detail || 'Dépôt échoué'); }
      const data = await depRes.json();
      setDeposit(data); setStep('done'); onSuccess?.();
    } catch (err) {
      setErrorMsg(err.message || 'Erreur inattendue.'); setStep('error');
    }
  };

  /* ── Success ── */
  if (step === 'done' && deposit) return (
    <div className="ua-section">
      <div className="ua-success-card">
        <FaCheckCircle className="ua-success-icon" />
        <h3>Dépôt soumis !</h3>
        <p>Votre dépôt de <strong>{parseFloat(deposit.amount).toLocaleString('fr-FR')} FC</strong> est en attente de validation.</p>
        <div className="ua-success-details">
          <div className="ua-sd-row"><span>Référence</span><strong>{deposit.reference}</strong></div>
          <div className="ua-sd-row"><span>Statut</span><StatusBadge status={deposit.status} /></div>
          <div className="ua-sd-row"><span>Date</span><span>{formatDate(deposit.created_at)}</span></div>
        </div>
        <button className="ua-btn-primary" onClick={reset}>Nouveau dépôt</button>
      </div>
    </div>
  );

  const isLoading = step === 'uploading' || step === 'submitting';

  return (
    <div className="ua-section">

      {/* ── Section title ── */}
      <div className="dep-section-title">
        <FaArrowDown />
        <span>Dépôt</span>
      </div>

      {/* ── Network cards: always visible, both shown ── */}
      <div className="dep-networks">
        {OPERATORS.map(op => (
          <div
            key={op.id}
            className={`dep-net ${selected.id === op.id ? 'dep-net--active' : ''}`}
            style={{ '--nc': op.color, '--nb': op.bg, '--nbr': op.border }}
            onClick={() => { setSelected(op); setErrorMsg(''); }}
          >
            <div className="dep-net-top">
              <div className="dep-net-indicator" />
              <div className="dep-net-info">
                <p className="dep-net-name">{op.name}</p>
                <p className="dep-net-number">{op.display}</p>
                <p className="dep-net-owner">{op.owner}</p>
              </div>
              <button
                className={`dep-net-copy ${copiedId === op.id ? 'dep-net-copy--ok' : ''}`}
                onClick={e => { e.stopPropagation(); copy(op); }}
              >
                {copiedId === op.id ? <FaCheck size={11} /> : <FaPaperclip size={11} />}
                {copiedId === op.id ? 'Copié' : 'Copier'}
              </button>
            </div>
            {selected.id === op.id && (
              <p className="dep-net-tip">
                Ouvrez <strong>{op.name}</strong> et envoyez l'argent à ce numéro, puis remplissez le formulaire.
              </p>
            )}
          </div>
        ))}
      </div>

      {/* ── Form ── */}
      <div className="ua-card">
        <p className="dep-form-label">Montant (FC)</p>
        <div className="dep-qa-row">
          {[10000,15000,20000,30000,50000].map(q => (
            <button key={q}
              className={`dep-qa ${parseFloat(amount) === q ? 'dep-qa--on' : ''}`}
              onClick={() => setAmount(String(q))} disabled={isLoading}>
              {q.toLocaleString('fr-FR')}
            </button>
          ))}
        </div>
        <div className="dep-input-row">
          <span className="dep-fc">FC</span>
          <input
            type="number" min="10000" placeholder="Autre montant"
            value={amount} onChange={e => setAmount(e.target.value)}
            disabled={isLoading} className="dep-input-amount"
          />
        </div>
        {amount && parseFloat(amount) < 10000 && (
          <p className="dep-min-warn">⚠️ Montant minimum : 10 000 FC</p>
        )}

        <p className="dep-form-label" style={{ marginTop: 16 }}>Capture d'écran</p>
        {!file ? (
          <div className="ua-dropzone"
            onClick={() => fileRef.current?.click()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            onDragOver={e => e.preventDefault()}>
            <FaPaperclip className="ua-dz-icon" />
            <p>Cliquez ou glissez votre capture</p>
            <span className="ua-dz-sub">JPEG · PNG · WebP · HEIC — max 10 Mo</span>
          </div>
        ) : (
          <div className="ua-preview-wrap">
            <img src={preview} alt="Preuve" className="ua-preview-img" />
            {!isLoading && (
              <button className="ua-preview-remove"
                onClick={() => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ''; }}>
                <FaTimes /> Changer
              </button>
            )}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif" hidden
          onChange={e => handleFile(e.target.files[0])} />

        {errorMsg && (
          <div className="ua-alert ua-alert-error" style={{ marginTop: 12 }}>
            <FaExclamationTriangle /> {errorMsg}
          </div>
        )}
        {step === 'uploading'  && <div className="ua-alert ua-alert-info" style={{ marginTop: 12 }}><Spinner /> Upload...</div>}
        {step === 'submitting' && <div className="ua-alert ua-alert-info" style={{ marginTop: 12 }}><Spinner /> Envoi...</div>}

        <button
          className={`ua-btn-primary ua-btn-full ${isLoading ? 'loading' : ''}`}
          style={{ marginTop: 18 }}
          onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <><Spinner /> En cours...</> : `Soumettre le dépôt`}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: WITHDRAWAL
═══════════════════════════════════════════════ */
function WithdrawSection({ accessToken, balance }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [amount, setAmount]           = useState('');
  const [mpesa, setMpesa]             = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [formError, setFormError]     = useState('');
  const [submitted, setSubmitted]     = useState(false);

  const load = useCallback(() => {
    if (!accessToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/withdrawals`, {
      credentials: 'include', headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(data => { setWithdrawals(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh when polling detects a withdrawal status change
  useEffect(() => accountEvents.on('withdrawals:refresh', load), [load]);

  const pending = withdrawals.find(w => w.status === 'pending');
  const history = withdrawals.filter(w => w.status !== 'pending');

  const handleSubmit = async () => {
    setFormError('');
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setFormError('Montant invalide.'); return; }
    if (amt > parseFloat(balance)) { setFormError('Solde insuffisant.'); return; }
    if (!mpesa.trim()) { setFormError('Numéro M-Pesa obligatoire.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/withdrawals`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ amount: amt, mpesa_number: mpesa.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || `Erreur ${res.status}`;
        setFormError(msg.toLowerCase().includes('pending') ? "Une demande est déjà en cours." : msg);
        setSubmitting(false); return;
      }
      setSubmitted(true); setAmount(''); setMpesa(''); load();
    } catch (e) {
      setFormError(e.message || 'Erreur réseau.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="ua-section">
      {pending && (
        <div className="ua-pending-banner">
          <FaClock style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Retrait en cours</strong>
            <p>Demande de {parseFloat(pending.amount).toFixed(2)} FC vers {pending.mpesa_number} — en attente de traitement.</p>
          </div>
        </div>
      )}

      <div className={`ua-card ${pending ? 'ua-card-disabled' : ''}`}>
        <h3 className="ua-card-title">Demande de retrait</h3>
        <p className="ua-card-desc">
          Solde disponible : <strong>{parseFloat(balance).toFixed(2)} FC</strong>
        </p>
        <div className="ua-field">
          <label>Montant à retirer (€)</label>
          <input type="number" placeholder="Ex : 50" min="1" value={amount}
            onChange={e => setAmount(e.target.value)} disabled={!!pending || submitting} />
        </div>
        <div className="ua-field">
          <label>Numéro M-Pesa</label>
          <input type="tel" placeholder="Ex : 0712345678" value={mpesa}
            onChange={e => setMpesa(e.target.value)} disabled={!!pending || submitting} />
        </div>
        {formError && <div className="ua-alert ua-alert-error"><FaExclamationTriangle /> {formError}</div>}
        {submitted && !pending && <div className="ua-alert ua-alert-success"><FaCheckCircle /> Demande envoyée !</div>}
        <button className={`ua-btn-primary ua-btn-full ${submitting ? 'loading' : ''}`}
          onClick={handleSubmit} disabled={!!pending || submitting}>
          {submitting ? <><Spinner /> En cours...</> : pending ? 'Demande en cours' : 'Soumettre le retrait'}
        </button>
      </div>

      {!loading && history.length > 0 && (
        <div className="ua-card">
          <div className="ua-card-header-row">
            <h3 className="ua-card-title">Historique retraits</h3>
            <button className="ua-icon-btn" onClick={load}><FaSync /></button>
          </div>
          <div className="ua-tx-list">
            {history.map(w => (
              <div key={w.id} className={`ua-tx-row ua-tx-${w.status === 'paid' ? 'success' : 'pending'}`}>
                <div className="ua-tx-left">
                  <div className="ua-tx-icon"><FaArrowUp /></div>
                  <div>
                    <p className="ua-tx-ref">{w.mpesa_number}</p>
                    <p className="ua-tx-date">{formatDate(w.created_at)}</p>
                  </div>
                </div>
                <div className="ua-tx-right">
                  <p className="ua-tx-amount ua-amount-out">-{parseFloat(w.amount).toFixed(2)} FC</p>
                  <span className={`ua-status-badge ${w.status === 'paid' ? 'badge-approved' : 'badge-pending'}`}>
                    {w.status === 'paid' ? 'Payé' : 'En attente'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {loading && <TransactionListSkeleton count={3} />}
      {error && <div className="ua-alert ua-alert-error"><FaExclamationTriangle /> {error}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: DEPOSITS HISTORY
═══════════════════════════════════════════════ */
function DepositsSection({ accessToken }) {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const load = useCallback(() => {
    if (!accessToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/deposits`, {
      credentials: 'include', headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(data => { setDeposits(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh when polling detects a deposit status change
  useEffect(() => accountEvents.on('deposits:refresh', load), [load]);

  return (
    <div className="ua-section">
      <div className="ua-card">
        <div className="ua-card-header-row">
          <h3 className="ua-card-title">Historique des dépôts</h3>
          <button className="ua-icon-btn" onClick={load}><FaSync /></button>
        </div>
        {loading && <TransactionListSkeleton count={4} />}
        {error   && <div className="ua-alert ua-alert-error"><FaExclamationTriangle /> {error}</div>}
        {!loading && !error && deposits.length === 0 && (
          <p className="ua-empty">Aucun dépôt pour le moment.</p>
        )}
        {!loading && !error && deposits.length > 0 && (
          <div className="ua-tx-list">
            {deposits.map(dep => (
              <div key={dep.id} className={`ua-tx-row ua-tx-${dep.status}`}>
                <div className="ua-tx-left">
                  <div className="ua-tx-icon"><FaArrowDown /></div>
                  <div>
                    <p className="ua-tx-ref">{dep.reference}</p>
                    <p className="ua-tx-date">{formatDate(dep.created_at)}</p>
                    {dep.admin_comment && dep.status === 'rejected' && (
                      <p className="ua-tx-comment">{dep.admin_comment}</p>
                    )}
                  </div>
                </div>
                <div className="ua-tx-right">
                  <p className="ua-tx-amount ua-amount-in">+{parseFloat(dep.amount).toFixed(2)} FC</p>
                  <StatusBadge status={dep.status} />
                  {dep.proof_image && (
                    <a href={`${API_BASE_URL}${dep.proof_image}`} target="_blank" rel="noreferrer" className="ua-proof-link">
                      <FaPaperclip /> Preuve
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: MY BETS
═══════════════════════════════════════════════ */
const BET_TABS = [
  { label: 'En cours', status: 'pending' },
  { label: 'Gagnés',   status: 'won'     },
  { label: 'Perdus',   status: 'lost'    },
];

const SEL_LABEL = { Home: '1', Draw: 'X', Away: '2' };

function BetsSection({ accessToken }) {
  const [tab, setTab]           = useState(0);
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchBets = useCallback((status) => {
    if (!accessToken) return;
    setLoading(true); setError(null);
    fetch(`${API_BASE_URL}/api/v1/bets?status=${status}`, {
      credentials: 'include', headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(data => { setTickets(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => { fetchBets(BET_TABS[tab].status); }, [tab, fetchBets]);

  // Auto-refresh when polling detects a bet status change
  useEffect(() => accountEvents.on('bets:refresh', () => fetchBets(BET_TABS[tab].status)), [tab, fetchBets]);

  return (
    <div className="ua-section">
      <div className="ua-seg-tabs">
        {BET_TABS.map((t, i) => (
          <button key={t.status} className={`ua-seg-tab ${tab === i ? 'active' : ''}`}
            onClick={() => { setTab(i); setTickets([]); }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <TicketListSkeleton count={3} />}
      {error && <div className="ua-alert ua-alert-error"><FaExclamationTriangle /> {error}</div>}
      {!loading && !error && tickets.length === 0 && (
        <div className="ua-empty-state">
          <FaTicketAlt className="ua-empty-icon" />
          <p>Aucun pari {BET_TABS[tab].label.toLowerCase()} pour le moment.</p>
        </div>
      )}

      {!loading && tickets.length > 0 && (
        <div className="ua-tickets">
          {tickets.map(ticket => {
            const isCombo = ticket.items?.length > 1;
            const potWin  = parseFloat(ticket.potential_win);
            const stake   = parseFloat(ticket.stake);
            return (
              <div key={ticket.id} className={`ua-ticket ua-ticket-${ticket.status}`}
                onClick={() => setSelected(ticket)} role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setSelected(ticket)}>
                <div className="ua-ticket-head">
                  <span className={`ua-tbadge ua-tbadge-${ticket.status}`}>
                    {ticket.status === 'won' ? <FaCheckCircle /> : ticket.status === 'lost' ? <FaTimesCircle /> : <FaClock />}
                    {ticket.status === 'won' ? ' Gagné' : ticket.status === 'lost' ? ' Perdu' : ' En cours'}
                  </span>
                  <span className="ua-ticket-type">{isCombo ? 'Combiné' : 'Simple'}</span>
                  <span className="ua-ticket-date">{formatDateTime(ticket.created_at)}</span>
                </div>
                <div className="ua-ticket-preview">
                  {ticket.items?.slice(0, 2).map(item => (
                    <div key={item.id} className="ua-ticket-item">
                      <span className="ua-ti-teams">{item.home_team} vs {item.away_team}</span>
                      <span className="ua-ti-sel">{SEL_LABEL[item.selection] ?? item.selection}</span>
                      <span className="ua-ti-odd">{parseFloat(item.odd_value).toFixed(2)}</span>
                    </div>
                  ))}
                  {ticket.items?.length > 2 && (
                    <p className="ua-ticket-more">+{ticket.items.length - 2} autre(s)</p>
                  )}
                </div>
                <div className="ua-ticket-foot">
                  <span>Mise : <strong>{stake.toFixed(2)} FC</strong></span>
                  <span>
                    {ticket.status === 'won'  ? <span className="ua-gain">+{potWin.toFixed(2)} FC</span>
                    : ticket.status === 'lost' ? <span className="ua-loss">-{stake.toFixed(2)} FC</span>
                    : <span className="ua-potential">{potWin.toFixed(2)} FC</span>}
                  </span>
                  <FaChevronRight className="ua-ticket-arrow" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="ua-modal-overlay" onClick={() => setSelected(null)}>
          <div className="ua-modal" onClick={e => e.stopPropagation()}>
            <button className="ua-modal-close" onClick={() => setSelected(null)}><FaTimes /></button>
            <h3 className="ua-modal-title">Détail du ticket</h3>
            <div className="ua-modal-summary">
              <div className="ua-ms-row"><span>Mise</span><strong>{parseFloat(selected.stake).toFixed(2)} FC</strong></div>
              <div className="ua-ms-row"><span>Cote totale</span><strong>{parseFloat(selected.total_odd).toFixed(2)}</strong></div>
              <div className="ua-ms-row">
                <span>Gain {selected.status === 'won' ? '' : 'potentiel'}</span>
                <strong className={selected.status === 'won' ? 'ua-gain' : selected.status === 'lost' ? 'ua-loss' : 'ua-potential'}>
                  {selected.status === 'lost'
                    ? `-${parseFloat(selected.stake).toFixed(2)} FC`
                    : `+${parseFloat(selected.potential_win).toFixed(2)} FC`}
                </strong>
              </div>
            </div>
            <div className="ua-modal-items">
              {selected.items?.map(item => (
                <div key={item.id} className={`ua-modal-item ua-modal-item-${item.status}`}>
                  <div className="ua-mi-main">
                    <span className="ua-mi-teams">{item.home_team} vs {item.away_team}</span>
                    <span className="ua-mi-league">{item.league}</span>
                  </div>
                  <div className="ua-mi-right">
                    <span className="ua-mi-sel">{SEL_LABEL[item.selection] ?? item.selection}</span>
                    <span className="ua-mi-odd">{parseFloat(item.odd_value).toFixed(2)}</span>
                    <span className="ua-mi-status">
                      {item.status === 'won' ? <FaCheckCircle color="#1db954" />
                        : item.status === 'lost' ? <FaTimesCircle color="#e53935" />
                        : <FaClock color="#f59e0b" />}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <p className="ua-modal-ref">Réf. #{selected.id}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: REFERRAL
═══════════════════════════════════════════════ */
const APP_URL = 'https://afri-bet.org';

function ReferralSection({ accessToken }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    fetch(`${API_BASE_URL}/api/v1/referral`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  const referralLink = data ? `${APP_URL}/register?ref=${data.referral_code}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(
      `Rejoins-moi sur AFRIBET et parie sur la World Cup ! Inscris-toi avec mon lien : ${referralLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading) return (
    <div className="ua-section">
      <div className="ua-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="mcs-bone mcs-line mcs-line-short" />
        <div className="mcs-bone mcs-line mcs-line-full" />
        <div className="mcs-bone mcs-line mcs-line-mid" />
        <div className="mcs-bone mcs-line mcs-line-full" style={{ height: 44, borderRadius: 10 }} />
      </div>
    </div>
  );
  if (error)   return <div className="ua-section"><div className="ua-alert ua-alert-error"><FaExclamationTriangle /> {error}</div></div>;
  if (!data)   return null;

  const qualified  = data.qualified_referrals ?? 0;
  const isChampion = data.is_champion_unlocked;
  const users      = data.referred_users ?? [];

  return (
    <div className="ua-section">

      {/* Champion banner */}
      {isChampion && (
        <div className="ua-ref-champion">
          <span className="ua-ref-champion-icon">🏆</span>
          <div>
            <p className="ua-ref-champion-title">Champion débloqué !</p>
            <p className="ua-ref-champion-sub">Accès au concours exclusif AFRIBET Champion. Félicitations !</p>
          </div>
        </div>
      )}

      {/* ── Stats + lien ── */}
      <div className="ua-card">
        <div className="ua-ref-header">
          <div>
            <h3 className="ua-card-title" style={{ marginBottom: 4 }}>Mon parrainage</h3>
            <p style={{ fontSize: 13, color: 'var(--ua-text-2)', margin: 0 }}>
              Code : <strong className="ua-ref-code">{data.referral_code}</strong>
            </p>
          </div>
          <div className="ua-ref-counter">
            <span className="ua-ref-counter-num">{qualified}</span>
            <span className="ua-ref-counter-lbl">{qualified <= 1 ? 'filleul qualifié' : 'filleuls qualifiés'}</span>
          </div>
        </div>

        <div className="ua-ref-link-box">
          <span className="ua-ref-link-text">{referralLink}</span>
        </div>

        <div className="ua-ref-actions">
          <button className="ua-btn-primary" onClick={handleCopy}>
            {copied ? <><FaCheck /> Copié !</> : 'Copier le lien'}
          </button>
          <button className="ua-ref-whatsapp-btn" onClick={handleWhatsApp}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </button>
        </div>
      </div>

      {/* ── Referred users ── */}
      <div className="ua-card">
        <h3 className="ua-card-title">
          Mes filleuls
          {users.length > 0 && <span className="ua-ref-count-badge">{users.length}</span>}
        </h3>

        {users.length === 0 ? (
          <div className="ua-ref-empty">
            <FaUserFriends className="ua-ref-empty-icon" />
            <p>Vous n'avez pas encore de filleuls.</p>
            <p className="ua-ref-empty-sub">
              Partagez votre lien — chaque ami qui s'inscrit et fait son premier dépôt compte comme filleul qualifié.
            </p>
          </div>
        ) : (
          <div className="ua-tx-list">
            {users.map((u, i) => (
              <div key={i} className="ua-tx-row">
                <div className="ua-tx-left">
                  <div className="ua-tx-icon ua-ref-user-icon">
                    {(u.username ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="ua-tx-ref">{u.username}</p>
                    <p className="ua-tx-date">Inscrit le {formatDate(u.created_at)}</p>
                  </div>
                </div>
                <span className="ua-status-badge badge-approved">Qualifié</span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: PROFILE
═══════════════════════════════════════════════ */
function ProfileSection({ user, accessToken }) {
  const fileRef = useRef(null);
  const [form, setForm]             = useState({ username: '', email: '', phone: '' });
  const [avatarPreview, setAvatar]  = useState(null);
  const [saved, setSaved]           = useState(false);

  useEffect(() => {
    if (user) setForm({ username: user.username ?? '', email: user.email ?? '', phone: user.phone_number ?? '' });
  }, [user]);

  const avatarUrl = avatarPreview
    ? avatarPreview
    : user?.image_file ? `${API_BASE_URL}${user.image_file}` : null;
  const initials = (user?.username ?? 'U').slice(0, 2).toUpperCase();

  return (
    <div className="ua-section">
      <div className="ua-card">
        {/* Avatar */}
        <div className="ua-profile-avatar-wrap">
          <div className="ua-profile-avatar"
            style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : {}}
            onClick={() => fileRef.current?.click()}>
            {!avatarUrl && <span className="ua-pa-initials">{initials}</span>}
            <div className="ua-pa-edit"><FaEdit size={12} /></div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden
            onChange={e => { const f = e.target.files[0]; if (f) setAvatar(URL.createObjectURL(f)); }} />
          <div className="ua-profile-meta">
            <h3>{user?.username ?? '—'}</h3>
            <p>{user?.email ?? '—'}</p>
            {user?.referral_code && (
              <p className="ua-referral">Code parrainage : <strong>{user.referral_code}</strong></p>
            )}
          </div>
        </div>

        <h4 className="ua-card-subtitle">Informations personnelles</h4>
        <div className="ua-form-grid">
          <div className="ua-field">
            <label>Nom d'utilisateur</label>
            <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
          </div>
          <div className="ua-field">
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="ua-field">
            <label>Téléphone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div className="ua-card-footer">
          {saved && <span className="ua-save-msg"><FaCheck /> Enregistré</span>}
          <button className="ua-btn-primary"
            onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2500); }}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: SECURITY
═══════════════════════════════════════════════ */
function SecuritySection() {
  const [notifs, setNotifs] = useState({ email: true, sms: false, promo: true, results: true });

  return (
    <div className="ua-section">
      <div className="ua-card">
        <h3 className="ua-card-title">Changer le mot de passe</h3>
        <div className="ua-form-grid">
          <div className="ua-field ua-field-full">
            <label>Mot de passe actuel</label>
            <input type="password" placeholder="••••••••" />
          </div>
          <div className="ua-field">
            <label>Nouveau mot de passe</label>
            <input type="password" placeholder="••••••••" />
          </div>
          <div className="ua-field">
            <label>Confirmer</label>
            <input type="password" placeholder="••••••••" />
          </div>
        </div>
        <div className="ua-card-footer">
          <button className="ua-btn-primary">Mettre à jour</button>
        </div>
      </div>

      <div className="ua-card">
        <h3 className="ua-card-title">Authentification 2FA</h3>
        <div className="ua-toggle-row">
          <div>
            <p className="ua-toggle-label">Protection renforcée</p>
            <p className="ua-toggle-desc">Recevez un code SMS à chaque connexion.</p>
          </div>
          <label className="ua-toggle">
            <input type="checkbox" defaultChecked />
            <span className="ua-toggle-track"><span className="ua-toggle-thumb" /></span>
          </label>
        </div>
      </div>

      <div className="ua-card">
        <h3 className="ua-card-title">Notifications</h3>
        <div className="ua-pref-list">
          {[
            { key: 'email',   label: 'Email',            desc: 'Confirmations et résultats' },
            { key: 'sms',     label: 'SMS',               desc: 'Alertes importantes' },
            { key: 'promo',   label: 'Promotions',        desc: 'Bonus et offres spéciales' },
            { key: 'results', label: 'Résultats live',    desc: 'Scores en temps réel' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="ua-toggle-row">
              <div>
                <p className="ua-toggle-label">{label}</p>
                <p className="ua-toggle-desc">{desc}</p>
              </div>
              <label className="ua-toggle">
                <input type="checkbox" checked={notifs[key]}
                  onChange={e => setNotifs({ ...notifs, [key]: e.target.checked })} />
                <span className="ua-toggle-track"><span className="ua-toggle-thumb" /></span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="ua-card">
        <h3 className="ua-card-title">Jeu responsable</h3>
        <div className="ua-form-grid">
          <div className="ua-field">
            <label>Limite dépôt journalier (€)</label>
            <input type="number" placeholder="Ex : 100" />
          </div>
          <div className="ua-field">
            <label>Limite perte hebdomadaire (€)</label>
            <input type="number" placeholder="Ex : 500" />
          </div>
        </div>
        <div className="ua-card-footer">
          <button className="ua-btn-primary">Appliquer</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SECTION: VERIFICATION
═══════════════════════════════════════════════ */
function VerificationSection() {
  const fileRef   = useRef(null);
  const [file, setFile]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null); // null | uploading | done

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setStatus('uploading');
    setTimeout(() => setStatus('done'), 2000);
  };

  return (
    <div className="ua-section">
      <div className="ua-card">
        <h3 className="ua-card-title">Vérification d'identité</h3>
        <p className="ua-card-desc">
          Envoyez votre pièce d'identité (CNI recto-verso ou passeport) pour activer toutes les fonctionnalités.
        </p>

        <div className="ua-verify-status-bar">
          <div className={`ua-vsb-step ${status ? 'done' : 'active'}`}>
            <div className="ua-vsb-circle">{status ? <FaCheck /> : '1'}</div>
            <span>Document envoyé</span>
          </div>
          <div className="ua-vsb-line" />
          <div className={`ua-vsb-step ${status === 'done' ? 'active' : ''}`}>
            <div className="ua-vsb-circle">2</div>
            <span>Vérification admin</span>
          </div>
          <div className="ua-vsb-line" />
          <div className="ua-vsb-step">
            <div className="ua-vsb-circle">3</div>
            <span>Compte vérifié</span>
          </div>
        </div>

        {!file ? (
          <div className="ua-dropzone ua-dz-large" onClick={() => fileRef.current?.click()}>
            <FaIdCard className="ua-dz-icon" />
            <p>Cliquez pour ajouter votre pièce d'identité</p>
            <span className="ua-dz-sub">JPEG, PNG, WebP ou PDF · Max 5 Mo</span>
          </div>
        ) : (
          <div className="ua-verify-preview">
            {file.type.startsWith('image/') && (
              <img src={preview} alt="Pièce d'identité" className="ua-verify-img" />
            )}
            <div className="ua-verify-info">
              <p className="ua-verify-filename">{file.name}</p>
              {status === 'uploading' && <p className="ua-verify-uploading"><Spinner /> Traitement...</p>}
              {status === 'done' && <p className="ua-verify-done"><FaCheckCircle /> Document reçu</p>}
              {status === 'done' && (
                <button className="ua-btn-ghost" onClick={() => { setFile(null); setPreview(null); setStatus(null); }}>
                  Remplacer
                </button>
              )}
            </div>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*,.pdf" hidden onChange={handleFile} />

        {status === 'done' && (
          <div className="ua-alert ua-alert-success">
            <FaCheckCircle /> Votre document a été soumis. Notre équipe le vérifie sous 24-48h.
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SIDEBAR NAV CONFIG
═══════════════════════════════════════════════ */
const NAV = [
  { id: 'overview',  label: 'Vue d\'ensemble', icon: FaHome },
  { id: 'deposit',   label: 'Dépôt',           icon: FaArrowDown },
  { id: 'withdraw',  label: 'Retrait',          icon: FaArrowUp },
  { id: 'deposits',  label: 'Historique',       icon: FaHistory },
  { id: 'bets',      label: 'Mes Paris',        icon: FaTicketAlt },
  { id: 'referral',  label: 'Parrainage',       icon: FaUserFriends },
  { id: 'profile',   label: 'Profil',           icon: FaUser },
  { id: 'security',  label: 'Sécurité',         icon: FaShieldAlt },
  { id: 'verify',    label: 'Vérification',     icon: FaIdCard },
];

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
export default function UserAccountPage() {
  const { user, balance, logout, fetchWallet, accessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Support ?section=deposit&operator=orange links from HomePage
  const initSection  = searchParams.get('section')  || 'overview';

  const [activeSection, setActiveSection] = useState(initSection);
  const [sidebarOpen, setSidebarOpen]     = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const goTo = (id) => { setActiveSection(id); setSidebarOpen(false); };

  const current = NAV.find(n => n.id === activeSection);

  return (
    <div className="ua-root">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="ua-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ── Sidebar ── */}
      <aside className={`ua-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <Link to="/" className="ua-sidebar-logo">
          <span className="ua-logo-text">Afri<span className="ua-logo-accent">Bet</span></span>
        </Link>

        {/* User card */}
        <div className="ua-sidebar-user">
          <div className="ua-su-avatar">
            {(user?.username ?? 'U').charAt(0).toUpperCase()}
          </div>
          <div className="ua-su-info">
            <p className="ua-su-name">{user?.username ?? 'Joueur'}</p>
            <p className="ua-su-balance">{Number(balance).toLocaleString('fr-FR')} FC</p>
          </div>
        </div>

      {/* Nav */}
        <nav className="ua-nav">
          <p className="ua-nav-section-label">Principal</p>
          {NAV.slice(0, 1).map(({ id, label, icon: Icon }) => (
            <button key={id}
              className={`ua-nav-item ${activeSection === id ? 'active' : ''}`}
              onClick={() => goTo(id)}>
              <Icon className="ua-nav-icon" />
              <span>{label}</span>
            </button>
          ))}

          <p className="ua-nav-section-label">Finances</p>
          {NAV.slice(1, 5).map(({ id, label, icon: Icon }) => (
            <button key={id}
              className={`ua-nav-item ${activeSection === id ? 'active' : ''}`}
              onClick={() => goTo(id)}>
              <Icon className="ua-nav-icon" />
              <span>{label}</span>
            </button>
          ))}

          <p className="ua-nav-section-label">Compte</p>
          {NAV.slice(5, 6).map(({ id, label, icon: Icon }) => (
            <button key={id}
              className={`ua-nav-item ${activeSection === id ? 'active' : ''}`}
              onClick={() => goTo(id)}>
              <Icon className="ua-nav-icon" />
              <span>{label}</span>
            </button>
          ))}

          <p className="ua-nav-section-label">Paramètres</p>
          {NAV.slice(6).map(({ id, label, icon: Icon }) => (
            <button key={id}
              className={`ua-nav-item ${activeSection === id ? 'active' : ''}`}
              onClick={() => goTo(id)}>
              <Icon className="ua-nav-icon" />
              <span>{label}</span>
            </button>
          ))}        </nav>

        {/* Footer */}
        <div className="ua-sidebar-footer">
          <Link to="/" className="ua-nav-item">
            <FaHome className="ua-nav-icon" />
            <span>Retour au site</span>
          </Link>
          <button className="ua-nav-item ua-logout" onClick={handleLogout}>
            <FaSignOutAlt className="ua-nav-icon" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="ua-main">
        {/* Top bar */}
        <div className="ua-topbar">
          <button className="ua-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Menu">
            <FaBars />
          </button>
          <h1 className="ua-page-title">{current?.label}</h1>
          <div className="ua-topbar-right">
            <div className="ua-topbar-balance">
              <span className="ua-tb-label">Solde</span>
              <span className="ua-tb-value">{Number(balance).toLocaleString('fr-FR')} FC</span>
            </div>
            <button className="ua-btn-deposit" onClick={() => goTo('deposit')}>+ Dépôt</button>
          </div>
        </div>

        {/* Content */}
        <div className="ua-content">
          {activeSection === 'overview'  && <OverviewSection user={user} balance={balance} onNavigate={goTo} />}
          {activeSection === 'deposit'   && <DepositSection  accessToken={accessToken} onSuccess={fetchWallet} />}
          {activeSection === 'withdraw'  && <WithdrawSection accessToken={accessToken} balance={balance} />}
          {activeSection === 'deposits'  && <DepositsSection accessToken={accessToken} />}
          {activeSection === 'bets'      && <BetsSection     accessToken={accessToken} />}
          {activeSection === 'referral'  && <ReferralSection accessToken={accessToken} />}
          {activeSection === 'profile'   && <ProfileSection  user={user} accessToken={accessToken} />}
          {activeSection === 'security'  && <SecuritySection />}
          {activeSection === 'verify'    && <VerificationSection />}
        </div>
      </main>
    </div>
  );
}
